
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { workflowService } from '@/api/services/workflowService';
import { Workflow } from '@/types/workflow';
import { RootState } from '@/store';
import { toast } from 'sonner';

interface WorkflowState {
    workflows: Workflow[];
    loading: boolean;
    error: string | null;
}

const initialState: WorkflowState = {
    workflows: [],
    loading: false,
    error: null,
};

// Async thunks
export const fetchWorkflows = createAsyncThunk(
    'workflow/fetchWorkflows',
    async () => {
        const response = await workflowService.getAllWorkflows();
        console.log(response.workflows);
        return response.workflows;
    }
);

export const deleteWorkflow = createAsyncThunk(
    'workflow/deleteWorkflow',
    async (workflowId: string, { rejectWithValue }) => {
        try {
            const response = await workflowService.deleteWorkflow(workflowId);
            return { ...response, workflowId };
        } catch (error: any) {
            console.error('Error deleting workflow:', error);
            return rejectWithValue(error.message || 'Failed to delete workflow');
        }
    }
);

export const duplicateWorkflow = createAsyncThunk(
    'workflow/duplicateWorkflow',
    async (workflowId: string) => {
        const response = await workflowService.duplicateWorkflow(workflowId);
        return response;
    }
);

const workflowSlice = createSlice({
    name: 'workflows',
    initialState,
    reducers: {
        setWorkflows: (state, action: PayloadAction<Workflow[]>) => {
            state.workflows = action.payload;
        },
        setWorkflow: (state, action: PayloadAction<Workflow>) => {
            state.workflows = [...state.workflows, action.payload];
        },
        updateWorkflow: (state, action: PayloadAction<Workflow>) => {
            state.workflows = state.workflows.map(workflow => workflow.id === action.payload.id ? action.payload : workflow);
        },
        updateWorkflowFolderId: (state, action: PayloadAction<{ workflowId: string; folderId: string }>) => {
            const { workflowId, folderId } = action.payload;
            state.workflows = state.workflows.map(workflow => workflow.id === workflowId ? { ...workflow, workflowFolderId: folderId } : workflow);
        }
    },
    extraReducers: (builder) => {
        // Fetch workflows
        builder
            .addCase(fetchWorkflows.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWorkflows.fulfilled, (state, action) => {
                state.loading = false;
                state.workflows = action.payload;
            })
            .addCase(fetchWorkflows.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch workflows';
            });
        // Delete workflow
        builder
            .addCase(deleteWorkflow.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteWorkflow.fulfilled, (state, action) => {
                state.loading = false;
                state.workflows = state.workflows.filter(workflow => workflow.id !== action.payload.workflowId);
                toast.success('Workflow deleted successfully');
            })
            .addCase(deleteWorkflow.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to delete workflow';
                toast.error('Failed to delete workflow');
            });
        // Duplicate workflow
        builder
            .addCase(duplicateWorkflow.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(duplicateWorkflow.fulfilled, (state, action) => {
                state.loading = false;
                state.workflows = [...state.workflows, action.payload];
                toast.success('Duplicated workflow successfully');
            })
            .addCase(duplicateWorkflow.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to duplicate workflow';
                toast.error('Failed to duplicate workflow');
            });
    },
});

export const { setWorkflows, setWorkflow, updateWorkflow, updateWorkflowFolderId } = workflowSlice.actions;
export default workflowSlice.reducer;


export const selectWorkflows = (state: RootState) => state.workflows;
export const selectWorkflowLoading = (state: RootState) => state.workflows.loading;
export const selectWorkflowError = (state: RootState) => state.workflows.error;